import {
  type CanvasElementFrom,
  type IoConfig,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type ElementSizingMode, type PropertiesConfig } from '@blaze-shared/elements-domain';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { AudioRecorderComponent } from './audio-recorder.component';
import {
  type AudioRecorderPropsModel,
  type AudioRecorderStyleMode,
  DEFAULT_RECORD_AUDIO_FILE_COLUMNS,
} from './audio-recorder.model';
import { AudioRecorderPropsBuilder } from './audio-recorder.props-builder';
import { DEFAULT_AUDIO_RECORDER_ICON_SIZE } from './audio-recorder.styles';
import { generateAudioRecorderFormField } from './generation/form-field';
import { audioRecorderV2PaddingToMarginMigration } from './migrations/002-audio-recorder-padding-to-margin.migration';

export type AudioRecorderDef = WithProps<AudioRecorderPropsModel> & WithIoConfig;
export type AudioRecorderElement = CanvasElementFrom<'audioRecorder'>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    audioRecorder: AudioRecorderDef;
  }
}

const DEFAULT_WIDTH = 200;
const MIN_SIZE = 48;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed'];

registerElementDefinition({
  type: 'audioRecorder',
  componentType: AudioRecorderComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'Audio Recorder',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: MIN_SIZE, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  toolbar: ['triggerClick'],
  tags: ['audio', 'recorder', 'voice'],
  propsBuilder: AudioRecorderPropsBuilder,
  generation: {
    formField: generateAudioRecorderFormField,
  },
  groups: ['custom'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: {
    type: 'input',
  },
  features: {
    loadingIndication: 'fadeWithoutIndicator',
    preventAutoHeight: true,
  },
  version: 2,
  migrations: {
    2: audioRecorderV2PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'File URL', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        schema: {
          columns: DEFAULT_RECORD_AUDIO_FILE_COLUMNS,
          id: '',
          name: 'audioRecorder',
        },
      },
    ],
  };
}

function getDefaults(): PropsOf<'audioRecorder'> {
  return {
    content: {
      button: {
        icon: 'icon_audio_record_24',
        iconStop: 'icon_audio_stop_24',
        label: 'Click to Record',
        labelStop: 'Recording...',
      },
      title: {
        enabled: false,
        label: 'Audio Recorder',
        position: 'left',
        align: 'left',
        width: '30%',
      },
      options: {
        recordMode: 'click',
      },
    },
    style: {
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },

      label: {
        // shared
        mode: 'idle',
        font: {
          fontFamily: '$t.fontFamily',
          fontSize: '$t.fontSize',
          lineHeight: '$t.lineHeight',
          style: [],
        },
        // idle
        idle: {
          color: { color1: '$t.title', selectorType: 'solid' },
          fontWeight: '$t.labelFontWeight',
          textStyle: [],
        },
        // recording
        recording: {
          color: { color1: '$t.title', selectorType: 'solid' },
          fontWeight: '$t.labelFontWeight',
          textStyle: [],
        },
      },

      button: {
        mode: 'idle',
        // shared
        iconPosition: 'left',
        iconSize: DEFAULT_AUDIO_RECORDER_ICON_SIZE,
        // idle
        idle: {
          fill: { color1: '$t.accent', selectorType: 'solid' },
          iconColor: { color1: '$t.accentLabel', selectorType: 'solid' },
        },
        // recording
        recording: {
          fill: { color1: '$t.accent', selectorType: 'solid' },
          iconColor: { color1: '$t.accentLabel', selectorType: 'solid' },
        },
      },

      background: {
        mode: 'idle',
        idle: {
          fill: { color1: '$t.fill', selectorType: 'none' },
          outline: { color1: '$t.outline', selectorType: 'none' },
          outlineRadius: '4px',
          outlineWidth: '1px',
        },
        recording: {
          fill: { color1: '$t.fill', selectorType: 'none' },
          outline: { color1: '$t.outline', selectorType: 'none' },
          outlineRadius: '4px',
          outlineWidth: '1px',
        },
      },
    },
  };
}

function getInitial(): PropsOf<'audioRecorder'> {
  const defaults: PropsOf<'audioRecorder'> = { ...getDefaults() };
  defaults.style.button.recording.fill = { color1: '#ef305e', selectorType: 'solid' };
  return defaults;
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('audioRecorder', [
    // Content
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'content.button',
        'Button',
        [
          epc.fields.textInput('content.button.label', 'Label'),
          epc.fields.iconField('content.button.icon'),
        ],
        {
          dataBindings: [
            {
              key: 'value',
              label: 'File URL',
              sources: 'field',
              bindings: [{ property: 'value', label: 'File URL', dataType: 'String' }],
            },
          ],
        },
      ),

      epc.subsections.subsection('content.options', 'Record Mode', [
        epc.fields.radiosField(
          'content.options.recordMode',
          'Record Mode',
          [
            { label: 'Click', value: 'click' },
            { label: 'Hold', value: 'hold' },
          ],
          { hideLabel: true },
        ),
        epc.utils.conditionalField(
          epc.fields.textInfo(
            'content.options.recordMode.info',
            'Click once to record. Click again to stop.',
            {
              classes: ['text-primary-darker font-medium'],
              fontSize: 13,
            },
          ),
          'content.options.recordMode',
          (type: 'click' | 'hold') => type === 'hold',
        ),
        epc.utils.conditionalField(
          epc.fields.textInfo(
            'content.options.recordMode.info',
            'Press and hold to record. Release to stop.',
            {
              classes: ['text-primary-darker font-medium'],
              fontSize: 13,
            },
          ),
          'content.options.recordMode',
          (type: 'click' | 'hold') => type === 'click',
        ),
      ]),

      epc.subsections.subsection('content', 'Action', [epc.fields.buttonActionField('')], {
        hideLabel: true,
      }),

      epc.subsections.elementSizeSubsection(
        MIN_SIZE,
        MAX_WIDTH,
        MAX_HEIGHT,
        WIDTH_OPTIONS,
        HEIGHT_OPTIONS,
      ),
      epc.subsections.visibilitySubsection(),
    ]),

    // Style
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        /* Label */
        epc.subsections.subsection('style.label', 'Label', [
          // Shared
          epc.fields.selectFieldFromFormFonts('style.label.font.fontFamily'),
          epc.fields.sliderField('style.label.font.fontSize', 'Size', [
            { min: 8, max: 48, unit: 'px' },
          ]),

          epc.fields.radiosField(
            'style.label.mode',
            'Behavior',
            [
              { label: 'Idle', value: 'idle' },
              { label: 'Recording', value: 'recording' },
            ],
            { hideLabel: true },
          ),

          // Idle
          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromFontWeights(
                'style.label.idle.fontWeight',
                'Font weight',
                'style.label.font.fontFamily',
              ),
              epc.fields.fontStyleField('style.label.idle.textStyle', 'Style', ['bold']),
              epc.fields.colorField('style.label.idle.color', 'Color', {
                allowGradient: false,
                allowNone: false,
              }),
            ],
            'style.label.mode',
            (mode: AudioRecorderStyleMode) => mode === 'recording',
          ),

          // Recording
          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromFontWeights(
                'style.label.recording.fontWeight',
                'Font weight',
                'style.label.font.fontFamily',
              ),
              epc.fields.fontStyleField('style.label.recording.textStyle', 'Style', ['bold']),
              epc.fields.colorField('style.label.recording.color', 'Color', {
                allowGradient: false,
                allowNone: false,
              }),
            ],
            'style.label.mode',
            (mode: AudioRecorderStyleMode) => mode === 'idle',
          ),
        ]),

        /* Button */
        epc.subsections.subsection('style.button', 'Button', [
          // Shared
          epc.fields.iconPositionField('style.button.iconPosition', 'content.button.icon'),
          epc.fields.sliderField('style.button.iconSize', 'Icon size', [
            { min: 8, max: 72, unit: 'px' },
          ]),

          epc.fields.radiosField(
            'style.button.mode',
            'Mode',
            [
              { label: 'Idle', value: 'idle' },
              { label: 'Recording', value: 'recording' },
            ],
            { hideLabel: true },
          ),
          // Idle
          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.button.idle.fill', 'Fill', {}),
              epc.fields.colorField('style.button.idle.iconColor', 'Icon', {
                allowGradient: false,
                allowNone: false,
              }),
            ],
            'style.button.mode',
            (mode: AudioRecorderStyleMode) => mode === 'recording',
          ),
          // Recording
          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.button.recording.fill', 'Fill', {}),
              epc.fields.colorField('style.button.recording.iconColor', 'Icon', {
                allowGradient: false,
                allowNone: false,
              }),
            ],
            'style.button.mode',
            (mode: AudioRecorderStyleMode) => mode === 'idle',
          ),
        ]),

        /* Background */
        epc.subsections.subsection('style.background', 'Background', [
          epc.fields.radiosField(
            'style.background.mode',
            'Mode',
            [
              { label: 'Idle', value: 'idle' },
              { label: 'Recording', value: 'recording' },
            ],
            { hideLabel: true },
          ),
          // Idle
          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.background.idle.fill', 'Fill', {}),
              epc.fields.colorField('style.background.idle.outline', 'Outline', {
                allowGradient: false,
                allowNone: true,
                hasOutlineSides: true,
                defaultValue: { color1: '#E2E2E2', selectorType: 'solid' },
              }),
              epc.fields.sliderField(`style.background.idle.outlineWidth`, 'Width', [
                {
                  min: 0,
                  max: 10,
                  unit: 'px',
                },
              ]),
              epc.fields.radiusField(`style.background.idle.outlineRadius`, undefined, {
                defaultValue: '25px',
              }),
            ],
            'style.background.mode',
            (mode: AudioRecorderStyleMode) => mode === 'recording',
          ),
          // Recording
          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.background.recording.fill', 'Fill', {}),
              epc.fields.colorField('style.background.recording.outline', 'Outline', {
                allowGradient: false,
                allowNone: true,
                hasOutlineSides: true,
                defaultValue: { color1: '#E2E2E2', selectorType: 'solid' },
              }),
              epc.fields.sliderField(`style.background.recording.outlineWidth`, 'Width', [
                {
                  min: 0,
                  max: 10,
                  unit: 'px',
                },
              ]),
              epc.fields.radiusField(`style.background.recording.outlineRadius`, undefined, {
                defaultValue: '25px',
              }),
            ],
            'style.background.mode',
            (mode: AudioRecorderStyleMode) => mode === 'idle',
          ),
        ]),
      ],
      true,
    ),
  ]);
}
